import { useEffect, useState, useRef } from 'react';

const DEFAULT_INTERACTION_DELAY = 100;

/**
 * Hook for deferred loading of non-critical content.
 * Triggers ONLY after the first user interaction.
 *
 * @param interactionDelayMs - Delay in milliseconds after the first
 * user interaction before triggering the load.
 */
export const useFirstInteraction = (
  interactionDelayMs: number = DEFAULT_INTERACTION_DELAY
) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const isTriggered = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const events = ['scroll', 'touchstart', 'mousemove'];

    const onInteraction = () => {
      if (isTriggered.current) return;
      isTriggered.current = true;

      events.forEach(event => {
        window.removeEventListener(event, onInteraction);
      });

      setTimeout(() => {
        setHasInteracted(true);
      }, interactionDelayMs);
    };

    events.forEach(event => {
      window.addEventListener(event, onInteraction, {
        passive: true,
        once: true
      });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, onInteraction);
      });
    };
  }, [interactionDelayMs]);

  return hasInteracted;
};
